import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface Language {
  code: string;
  name: string;
  countryCode: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', countryCode: 'gb' },
  { code: 'es', name: 'Español', countryCode: 'es' },
  { code: 'de', name: 'Deutsch', countryCode: 'de' },
  { code: 'ru', name: 'Русский', countryCode: 'ru' },
];

const LANGUAGE_STORAGE_KEY = 'axetra_preferred_language';

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();

  const currentLanguage = languages.find((l) => l.code === (lang || i18n.language)) || languages[0];

  const changeLanguage = async (newLang: string) => {
    // Save to localStorage
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
    } catch (error) {
      console.warn('Failed to save language preference to localStorage:', error);
    }

    // For public pages with /:lang in URL, navigate to new language URL
    if (lang) {
      // Replace the language part of the URL
      const pathWithoutLang = location.pathname.replace(`/${lang}`, '');
      const newPath = `/${newLang}${pathWithoutLang}`;
      navigate(newPath);
    } else {
      // For authenticated/dashboard pages, just change i18n language
      await i18n.changeLanguage(newLang);
    }

    // Sync with backend if authenticated
    if (isAuthenticated) {
      try {
        await api.put('/api/user/locale', { locale: newLang });
      } catch (error) {
        console.error('Failed to update language preference on server:', error);
      }
    }

    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Change language"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <img
            src={`https://flagcdn.com/w20/${currentLanguage.countryCode}.png`}
            srcSet={`https://flagcdn.com/w40/${currentLanguage.countryCode}.png 2x`}
            alt={currentLanguage.name}
            className="max-w-full max-h-full rounded"
          />
        </div>
        <span className="text-sm font-medium text-gray-700">{i18n.language.toUpperCase()}</span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50
                  transition-colors first:rounded-t-lg last:rounded-b-lg
                  ${i18n.language === lang.code ? 'bg-gray-50' : ''}
                `}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <img
                    src={`https://flagcdn.com/w20/${lang.countryCode}.png`}
                    srcSet={`https://flagcdn.com/w40/${lang.countryCode}.png 2x`}
                    alt={lang.name}
                    className="max-w-full max-h-full rounded"
                  />
                </div>
                <span className="text-sm font-medium">{lang.name}</span>
                {i18n.language === lang.code && (
                  <svg
                    className="ml-auto w-4 h-4 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
